import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import L1 from '@/assets/v2/screen2-1.svg?react';
import L2 from '@/assets/v2/screen2-2.svg?react';
import L3 from '@/assets/v2/screen2-3.svg?react';
import R1EN from '@/assets/v2/screen2-list1-en.png';
import R1ZH from '@/assets/v2/screen2-list1-zh.png';
import R2EN from '@/assets/v2/screen2-list2-en.png';
import R2ZH from '@/assets/v2/screen2-list2-zh.png';
import R3EN from '@/assets/v2/screen2-list3-en.png';
import R3ZH from '@/assets/v2/screen2-list3-zh.png';
import RevealText from '@/v2/components/RevealText';

const SplitLine = () => <div className="bg-border-split mt-0 h-px w-full"></div>;

// Feature 类型定义
interface Feature {
  id: number;
  icon: React.ReactNode;
  titleKey: string;
  descriptionKey: string;
  src: string;
}

// 判断是否为中文语言
const isChinese = (language: string) => {
  return ['zh', 'zh-CN', 'zh-Hans', 'zh-Hant', 'zh-TW', 'zh-HK'].some((lang) =>
    language.startsWith(lang),
  );
};

// 第二屏的基础数据配置（不含 src）
const FEATURE_BASE = [
  {
    id: 1,
    icon: <L1 />,
    titleKey: 'home.screen2.features.planning.title',
    descriptionKey: 'home.screen2.features.planning.description',
    srcBase: 'screen2-1',
  },
  {
    id: 2,
    icon: <L2 />,
    titleKey: 'home.screen2.features.multitasking.title',
    descriptionKey: 'home.screen2.features.multitasking.description',
    srcBase: 'screen2-2',
  },
  {
    id: 3,
    icon: <L3 />,
    titleKey: 'home.screen2.features.endToEnd.title',
    descriptionKey: 'home.screen2.features.endToEnd.description',
    srcBase: 'screen2-3',
  },
];

// 小屏幕下的平铺列表组件 (< 1000px)
// 每个feature包含：标题+描述+对应图片，垂直平铺展示
const MobileFeatureList = ({ features }: { features: Feature[] }) => {
  const { t } = useTranslation();

  return (
    <div className="w-full">
      {/* 标题部分 */}
      <div className="border-border-secondary mb-16 border-b pb-8">
        <div className="text-content-title mb-4 text-[32px] font-bold leading-[1.2]">
          <RevealText text={t('home.screen2.title')} />
        </div>
        <p className="text-content-secondary text-sm md:text-base">{t('home.screen2.subtitle')}</p>
      </div>

      {/* 平铺的功能列表 - 每个feature包含标题+描述+图片 */}
      <div className="flex flex-col gap-8">
        {features.map((feature, index) => (
          <div key={feature.id} className="flex flex-col gap-[16px]">
            {/* 标题行 */}
            <div className="flex items-center gap-3 text-xl">
              <span className="text-brand bg-brand/10 flex h-9 w-10 items-center justify-center rounded-lg">
                {feature.icon}
              </span>
              <span className="text-content-primary font-semibold">{t(feature.titleKey)}</span>
            </div>

            {/* 描述 */}
            <p className="text-content-secondary text-sm md:text-base">
              {t(feature.descriptionKey)}
            </p>

            {/* 对应的图片区域 */}
            <div className={`mt-[16px] ${index < features.length - 1 && 'mb-[32px]'}`}>
              {/* <div className="overflow-hidden bg-gradient-to-br rounded-2xl from-cyan-400/20 to-blue-500/20"> */}
              <div className="flex min-h-[280px] items-center justify-center bg-white/5">
                {/* 这里用文字代替图片，后续可替换为实际图片 */}
                <img src={feature.src} className="h-auto w-full object-contain" />
              </div>
              {/* </div> */}
            </div>
            {index < features.length - 1 && <SplitLine />}
          </div>
        ))}
      </div>
    </div>
  );
};

// 大屏幕下的第二屏组件 (>= 1000px)
const DesktopSecondScreen = ({
  activeIndex,
  onItemClick,
  features,
}: {
  activeIndex: number;
  onItemClick: (index: number) => void;
  features: Feature[];
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex h-full items-start justify-center">
      <div className=" 3xl:gap-24 3xl:scale-110 3xl:origin-center flex max-w-screen-xl items-start gap-8 lg:gap-[108px] 2xl:gap-[108px]">
        {/* 左侧：标题和列表 */}
        <div className="w-[476px] min-w-0 flex-1">
          {/* 标题部分 - 固定高度防止列表高度变化影响 */}
          <div className="mb-8 lg:mb-12">
            <div className="text-content-title text-wrap text-[40px] font-semibold leading-[1.2]">
              <RevealText text={t('home.screen2.title')} />
            </div>
            <p className="text-content-title mt-4 text-[14px] font-normal leading-[1.2] opacity-80">
              {t('home.screen2.subtitle')}
            </p>
          </div>

          <SplitLine />
          {/* 功能列表 */}
          <div className="">
            {features.map((feature, index) => (
              <Fragment key={feature.id}>
                <div
                  onClick={() => onItemClick(index)}
                  className={`flex cursor-pointer flex-col items-start gap-3 rounded-lg py-8 transition-all duration-300 lg:gap-4 ${activeIndex === index ? '' : 'can-hover:hover:opacity-60 opacity-30'}`}
                >
                  <div className="flex items-center gap-3">
                    {feature.icon}
                    <div
                      className={`text-content-primary text-[20px] font-semibold leading-[1.2] transition-colors`}
                    >
                      {t(feature.titleKey)}
                    </div>
                  </div>
                  {activeIndex === index && (
                    <span className="text-content-primary mt-1 text-sm leading-normal opacity-80">
                      {t(feature.descriptionKey)}
                    </span>
                  )}
                </div>
                <SplitLine />
              </Fragment>
            ))}
          </div>
        </div>

        {/* 右侧：内容展示区域 - 响应式尺寸，使用clamp动态缩放 */}
        <div className="relative w-[699px] min-w-0 grow-0">
          {/*
            右侧图尺寸随屏幕宽度动态缩放:
            - 1000px时: 约320px高度
            - 1728px时: 约500px高度
            - 2560px时: 约600px高度
            使用 clamp(最小值, 首选值, 最大值) 实现平滑过渡
          */}
          <div
            className="flex items-center justify-center"
            style={{
              height: 'clamp(320px, calc(200px + 20vw), 600px)',
              minWidth: 'clamp(300px, calc(150px + 25vw), 700px)',
            }}
          >
            <div className="relative size-full text-center">
              {/* 内容切换动画 */}
              {features.map((feature, index) => (
                <div
                  key={feature.id}
                  className={`absolute inset-0 flex size-full items-center justify-center transition-all duration-300 ${
                    activeIndex === index
                      ? 'translate-y-0 opacity-100'
                      : 'pointer-events-none translate-y-4 opacity-0'
                  }`}
                >
                  <img className="size-full object-contain" src={feature.src}></img>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 内部切换组件
const SecondScreenContent = ({
  activeIndex,
  onItemClick,
  isFlat,
  features,
}: {
  activeIndex: number;
  onItemClick: (index: number) => void;
  isFlat: boolean;
  features: Feature[];
}) => {
  if (isFlat) {
    return <MobileFeatureList features={features} />;
  }

  return (
    <DesktopSecondScreen activeIndex={activeIndex} onItemClick={onItemClick} features={features} />
  );
};

/**
 * 第二屏组件 - 包含滚动劫持逻辑
 */
export const SecondScreen = ({ isFlat }: { isMobile: boolean; isFlat: boolean }) => {
  const { i18n } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  // 标记是否正在程序化滚动，避免滚动事件干扰
  // const isScrollingRef = useRef(false);

  // 根据语言生成 features 数据
  const features = useMemo<Feature[]>(() => {
    const getUrl = (id: number) => {
      if (id === 1) {
        return isChinese(i18n.language) ? R1ZH : R1EN;
      } else if (id === 2) {
        return isChinese(i18n.language) ? R2ZH : R2EN;
      } else if (id === 3) {
        return isChinese(i18n.language) ? R3ZH : R3EN;
      } else {
        return '';
      }
    };
    return FEATURE_BASE.map((item) => ({
      id: item.id,
      icon: item.icon,
      titleKey: item.titleKey,
      descriptionKey: item.descriptionKey,
      src: getUrl(item.id),
    }));
  }, [i18n.language]);

  // 简单的点击切换（无滚动劫持）
  const handleItemClick = useCallback(
    (index: number) => {
      setActiveIndex(index);
      setAutoPlay(false); // 用户点击时停止自动播放

      // 3秒后恢复自动播放
      setTimeout(() => {
        if (isInView) {
          setAutoPlay(true);
        }
      }, 3000);
    },
    [isInView],
  );

  // 启动定时器，每3秒切换到下一个item
  const startTimer = useCallback(() => {
    // 只有在可视区域内且桌面端才启动定时器
    if (!isInView || isFlat) return;

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const newIndex = (prev + 1) % features.length;
        return newIndex;
      });
    }, 3000);
  }, [isInView, isFlat, features.length]);

  // 停止定时器
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  // 使用 IntersectionObserver 监听组件是否在可视区域内
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.3, // 当30%可见时触发
      },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // 根据 isInView 和 autoPlay 状态控制定时器
  useEffect(() => {
    if (isInView && autoPlay && !isFlat) {
      startTimer();
    } else {
      stopTimer();
    }

    return () => {
      stopTimer();
    };
  }, [isInView, autoPlay, isFlat, startTimer, stopTimer]);
  return (
    <div ref={sectionRef} className="mx-auto w-full px-[28px]  md:px-[120px]  lg:px-0">
      <SecondScreenContent
        isFlat={isFlat}
        activeIndex={activeIndex}
        onItemClick={handleItemClick}
        features={features}
      />
    </div>
  );
};

export default SecondScreen;
